import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { extractApiError } from '@/api/client';
import {
  useInitiateLeaseSign,
  useLease,
  useVerifyLeaseSign,
} from '@/hooks/useLeases';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 45;

/**
 * Tenant lease signing (web). Mirrors the auth OtpPage: backend sends a 6-digit
 * SMS code to the tenant's phone via POST /leases/:id/sign/initiate; entering it
 * (POST /leases/:id/sign/verify) signs the contract and activates the lease.
 */
export function LeaseSignPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const validId = Number.isFinite(id) && id > 0;

  const lease = useLease(validId ? id : null);
  const initiate = useInitiateLeaseSign(id);
  const verify = useVerifyLeaseSign(id);

  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const initiatedRef = useRef(false);

  // Авто-запрос OTP один раз при заходе на экран.
  useEffect(() => {
    if (!validId || initiatedRef.current) return;
    initiatedRef.current = true;
    initiate.mutate(undefined, {
      onError: (err) => setError(extractApiError(err).message),
    });
  }, [validId, initiate]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = window.setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearInterval(timer);
  }, [secondsLeft]);

  const submit = async (code: string) => {
    setError(null);
    try {
      await verify.mutateAsync(code);
      navigate('/', { replace: true });
    } catch (err) {
      setError(extractApiError(err).message);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputs.current[0]?.focus();
    }
  };

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < OTP_LENGTH - 1) inputs.current[index + 1]?.focus();
    if (next.every((d) => d.length === 1)) void submit(next.join(''));
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const resend = async () => {
    setError(null);
    try {
      await initiate.mutateAsync();
      setSecondsLeft(RESEND_SECONDS);
    } catch (err) {
      setError(extractApiError(err).message);
    }
  };

  if (!validId) return <Navigate to="/" replace />;

  const address = lease.data?.property?.address;

  return (
    <div className="mx-auto max-w-md">
      <Card title={t('leases.sign.title')}>
        {address ? <p className="mb-1 text-sm font-medium text-slate-800">{address}</p> : null}
        <p className="mb-4 text-sm text-slate-600">{t('leases.sign.hint')}</p>

        <div className="mb-4 flex gap-2">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => {
                inputs.current[i] = el;
              }}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={verify.isPending}
              className="h-12 w-12 rounded-lg border border-slate-200 text-center text-lg focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-50"
              autoFocus={i === 0}
            />
          ))}
        </div>

        {verify.isPending ? (
          <div className="mb-3 flex items-center gap-2 text-sm text-slate-500">
            <Spinner /> {t('common.loading')}
          </div>
        ) : null}
        {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}

        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={() => void resend()}
          disabled={secondsLeft > 0 || initiate.isPending}
        >
          {secondsLeft > 0
            ? `${t('auth.resend_in', { seconds: secondsLeft })}`
            : t('auth.resend_code')}
        </Button>
      </Card>
    </div>
  );
}
